import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { Layout } from '../../components/Layout';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { apiService } from '../../services/api';
import type { BudgetDashboardResponse } from '../../../../shared/types';

const COLORS = {
  uploadTier: '#3B82F6', // Blue
  archiveTier: '#10B981', // Green
  cloudFront: '#F59E0B', // Amber
  total: '#6366F1', // Indigo
};

const TIME_PERIODS = [
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
] as const;

interface CostBreakdownData {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number; // Index signature for Recharts compatibility
}

const BudgetDashboardContent: React.FC = () => {
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<7 | 30 | 90>(30);

  const {
    data: budgetData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['budgetDashboard', selectedTimePeriod],
    queryFn: () => apiService.getBudgetDashboard({ timePeriod: selectedTimePeriod }),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getCostBreakdownData = (data: BudgetDashboardResponse): CostBreakdownData[] => {
    return [
      {
        name: 'Upload Tier (S3 Standard)',
        value: data.uploadTierCost,
        color: COLORS.uploadTier,
      },
      {
        name: 'Archive Tier (Glacier)',
        value: data.archiveTierCost,
        color: COLORS.archiveTier,
      },
      {
        name: 'CloudFront Data Transfer',
        value: data.cloudFrontCost,
        color: COLORS.cloudFront,
      },
    ].filter(item => item.value > 0);
  };

  const renderCustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{`Date: ${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formatCurrency(entry.value || 0)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{data.name}</p>
          <p className="text-sm" style={{ color: data.color }}>
            {`Cost: ${formatCurrency(data.value || 0)}`}
          </p>
          <p className="text-sm text-gray-600">
            {`${(((data.value || 0) / budgetData!.totalCost) * 100).toFixed(1)}% of total`}
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white shadow rounded-lg p-6">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Budget Data</h3>
            <p className="text-red-600 mb-4">
              {error instanceof Error ? error.message : 'Failed to load budget dashboard data'}
            </p>
            <button
              onClick={() => refetch()}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!budgetData) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">No Data Available</h3>
            <p className="text-yellow-600">
              Budget data is not available at this time. Please try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const costBreakdownData = getCostBreakdownData(budgetData);

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Budget Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">
              Last updated: {new Date(budgetData.lastUpdated).toLocaleString()}
            </p>
          </div>
          
          {/* Time Period Selector */}
          <div className="flex items-center space-x-2">
            <label htmlFor="timePeriod" className="text-sm font-medium text-gray-700">
              Time Period:
            </label>
            <select
              id="timePeriod"
              value={selectedTimePeriod}
              onChange={(e) => setSelectedTimePeriod(Number(e.target.value) as 7 | 30 | 90)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {TIME_PERIODS.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Cost Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(budgetData.totalCost)}
                </p>
              </div>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.total }}></div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">Upload Tier</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(budgetData.uploadTierCost)}
                </p>
              </div>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.uploadTier }}></div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">Archive Tier</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(budgetData.archiveTierCost)}
                </p>
              </div>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.archiveTier }}></div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">CloudFront</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(budgetData.cloudFrontCost)}
                </p>
              </div>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.cloudFront }}></div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Cost Trend Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Cost Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={budgetData.costTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  tickFormatter={(value) => `$${value.toFixed(2)}`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={renderCustomTooltip} />
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke={COLORS.total}
                  strokeWidth={2}
                  dot={{ fill: COLORS.total, strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Cost Breakdown Pie Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Cost Breakdown</h3>
            {costBreakdownData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={costBreakdownData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: any) => 
                      `${name || ''}: ${((percent || 0) * 100).toFixed(1)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {costBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={renderPieTooltip} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <p>No cost data available for the selected period</p>
              </div>
            )}
          </div>
        </div>

        {/* Storage Tier Comparison Bar Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Storage Tier Cost Comparison</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                {
                  name: 'Upload Tier (S3 Standard)',
                  cost: budgetData.uploadTierCost,
                  fill: COLORS.uploadTier,
                },
                {
                  name: 'Archive Tier (Glacier)',
                  cost: budgetData.archiveTierCost,
                  fill: COLORS.archiveTier,
                },
                {
                  name: 'CloudFront Transfer',
                  cost: budgetData.cloudFrontCost,
                  fill: COLORS.cloudFront,
                },
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tickFormatter={(value) => `$${value.toFixed(2)}`}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: any) => [formatCurrency(value || 0), 'Cost']}
                labelStyle={{ color: '#374151' }}
              />
              <Bar dataKey="cost" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Insights */}
        <div className="bg-white shadow rounded-lg p-6 mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Cost Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Storage Efficiency</h4>
              <p className="text-sm text-gray-600">
                {budgetData.archiveTierCost > budgetData.uploadTierCost
                  ? 'Most of your costs are from archived storage. Consider reviewing archival policies.'
                  : 'Your archival strategy is working well - archived storage costs are lower than active storage.'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Data Transfer</h4>
              <p className="text-sm text-gray-600">
                {budgetData.cloudFrontCost > budgetData.totalCost * 0.1
                  ? 'CloudFront transfer costs are significant. Monitor download patterns.'
                  : 'CloudFront transfer costs are reasonable relative to storage costs.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const BudgetDashboardPage: React.FC = () => {
  return (
    <ProtectedRoute requireSystemAdmin={true}>
      <Layout>
        <BudgetDashboardContent />
      </Layout>
    </ProtectedRoute>
  );
};